import { Fragment, useEffect, useId, useRef } from 'react'
import type { JSX } from 'react'
import type { Block, Format, Inline, Selection as TextSelection } from './markdown'
import { applyFormat, parseMarkdown } from './markdown'

/**
 * The editor in the approval queue — S-D5's `sx-editor`, over markdown.
 *
 * **Source on the left, preview on the right, and that is the one place this
 * departs from the drawn screen.** The prototype's editor is a `contenteditable`
 * div showing rendered prose, which is the right drawing for a wireframe and the
 * wrong implementation for this API: `EditsIn.body_markdown` says *"markdown,
 * which is the stored source of truth — what you save here is what publishes"*.
 * A `contenteditable` would mean serialising a DOM back to markdown on every
 * keystroke, and the round trip is lossy in exactly the way that matters here —
 * a heading that comes back as bold text is a published page with no `h2`, which
 * is a finding `app/seo/findings.py` then files against the tenant's own site.
 * So the tenant edits what is stored, and sees what it becomes.
 *
 * **Nothing here uses `dangerouslySetInnerHTML`.** `parseMarkdown` answers data
 * and this file turns that data into React elements, so the model-written text
 * on its way to a customer's website never passes through an HTML string. See
 * `markdown.ts` for the argument and for `safeHref`, which is the only value in
 * the preview that is not a text node.
 *
 * Three states, as CLAUDE.md requires of every component. **Empty** is a preview
 * with nothing in it, which says so rather than showing a blank panel. **Loading**
 * is `busy` — the save is in flight, the text is locked so a keystroke cannot be
 * lost between the request and its answer, and the bar says which. **Error** is
 * `error`, shown against the editor itself; the page shows its own `InlineError`
 * for the failed request, and this is the line that says *these words were not
 * saved*.
 */
export function MarkdownEditor({
  label,
  value,
  onChange,
  busy = false,
  disabled = false,
  error,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  /** A save is in flight. Locks the text and says so. */
  busy?: boolean | undefined
  disabled?: boolean | undefined
  /** Why the last attempt to save these words did not stick. */
  error?: string | undefined
}): JSX.Element {
  const sourceId = useId()
  const source = useRef<HTMLTextAreaElement>(null)
  const pending = useRef<TextSelection | null>(null)
  const locked = busy || disabled

  // The caret, restored after the render that the format caused. It cannot be
  // set inside the click handler: React owns `value`, and assigning a textarea's
  // value moves the caret to the end — so the selection has to be re-applied on
  // the far side of the update, not before it.
  useEffect(() => {
    const next = pending.current
    const area = source.current
    if (next === null || area === null) return
    pending.current = null
    area.focus()
    area.setSelectionRange(next.start, next.end)
  })

  function press(format: Format): void {
    const area = source.current
    if (area === null || locked) return
    const edit = applyFormat(format, area.value, {
      start: area.selectionStart,
      end: area.selectionEnd,
    })
    pending.current = { start: edit.start, end: edit.end }
    onChange(edit.value)
  }

  const blocks = parseMarkdown(value)

  return (
    <div className="app-editor">
      <div className="app-editor__bar" role="toolbar" aria-label={`Formatting — ${label}`}>
        {TOOLS.map((tool) => (
          <button
            key={tool.format}
            type="button"
            aria-label={tool.label}
            aria-controls={sourceId}
            disabled={locked}
            // Keep the selection: a press that stole focus first would format
            // whatever the textarea happened to have selected before, which on
            // a second press in a row is nothing.
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={() => {
              press(tool.format)
            }}
          >
            {tool.format === 'bold' ? (
              <strong>{tool.glyph}</strong>
            ) : tool.format === 'italic' ? (
              <em>{tool.glyph}</em>
            ) : (
              tool.glyph
            )}
          </button>
        ))}
        <span className="ar-card__spacer" />
        <span className="ar-subtle app-editor__note">
          {busy ? 'Saving…' : disabled ? 'Read only' : 'Editing enabled'}
        </span>
      </div>

      {error === undefined ? null : (
        <p className="ar-field__error app-editor__error" role="alert">
          {error}
        </p>
      )}

      <div className="app-editor__panes">
        <textarea
          id={sourceId}
          ref={source}
          className="app-editor__source"
          aria-label={label}
          spellCheck
          value={value}
          disabled={locked}
          onChange={(event) => {
            onChange(event.currentTarget.value)
          }}
        />
        <div className="app-editor__body" role="region" aria-label={`${label} — preview`}>
          {blocks.length === 0 ? (
            <p className="ar-subtle app-editor__blank">
              Nothing to preview. What you write on the left is what publishes.
            </p>
          ) : (
            blocks.map((block, index) => <Rendered key={index} block={block} />)
          )}
        </div>
      </div>
    </div>
  )
}

/** The six presses S-D5's bar draws, in its order. */
const TOOLS: readonly { format: Format; label: string; glyph: string }[] = [
  { format: 'bold', label: 'Bold', glyph: 'B' },
  { format: 'italic', label: 'Italic', glyph: 'I' },
  { format: 'h2', label: 'Heading', glyph: 'H2' },
  { format: 'h3', label: 'Sub-heading', glyph: 'H3' },
  { format: 'link', label: 'Link', glyph: 'Link' },
  { format: 'list', label: 'List', glyph: 'List' },
]

/** One parsed block, as elements. */
function Rendered({ block }: { block: Block }): JSX.Element {
  switch (block.kind) {
    case 'h2':
      return (
        <h2>
          <Spans spans={block.spans} />
        </h2>
      )
    case 'h3':
      return (
        <h3>
          <Spans spans={block.spans} />
        </h3>
      )
    case 'p':
      return (
        <p>
          <Spans spans={block.spans} />
        </p>
      )
    case 'ul':
      return (
        <ul>
          {block.items.map((item, index) => (
            <li key={index}>
              <Spans spans={item} />
            </li>
          ))}
        </ul>
      )
    case 'ol':
      return (
        <ol>
          {block.items.map((item, index) => (
            <li key={index}>
              <Spans spans={item} />
            </li>
          ))}
        </ol>
      )
  }
}

/**
 * The inline runs of one block.
 *
 * `rel="noreferrer"` on every link, and `target="_blank"` because a preview is
 * something you glance at beside the text you are editing — following a link
 * out of it would lose unsaved words.
 */
function Spans({ spans }: { spans: Inline[] }): JSX.Element {
  return (
    <>
      {spans.map((span, index) => {
        switch (span.kind) {
          case 'strong':
            return <strong key={index}>{span.text}</strong>
          case 'em':
            return <em key={index}>{span.text}</em>
          case 'code':
            return (
              <code key={index} className="ar-mono">
                {span.text}
              </code>
            )
          case 'link':
            return (
              <a key={index} className="ar-link" href={span.href} target="_blank" rel="noreferrer">
                {span.text}
              </a>
            )
          case 'text':
            return <Fragment key={index}>{span.text}</Fragment>
        }
      })}
    </>
  )
}
